/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { arrayify } from '@gh/core-util';
import { ITreeOptions, ITreeState, TreeNode } from '@ali-hm/angular-tree-component';
import {
  clamp,
  compact,
  forEachRight,
  fromPairs,
  identity,
  initial,
  last,
  pickBy,
  size,
  without,
} from 'lodash';

export function createDefaultTreeOptions(options: ITreeOptions): ITreeOptions {
  const { nodeClass: baseNodeClass } = options;

  return {
    ...options,
    nodeClass: (node) => {
      const defaultClass = Object.keys(pickBy(
        {
          'node-in-draggable': node.level > 1 && node.parent && node.parent.allowDrag(),
          'node-draggable': node.allowDrag(),
          'node-expandable': node.hasChildren,
          'node-draggable-sibling': false,
        },
        identity)).join(' ');

      return baseNodeClass ? `${defaultClass} ${baseNodeClass(node)}` : defaultClass;
    },
  };
}

export function treeOptionsEnableDragging(options: ITreeOptions, enabled: boolean): ITreeOptions {
  return { ...options, allowDrag: enabled ? options.allowDrag || true : false };
}

export function isTreeNode(node: any): node is TreeNode {
  return node instanceof TreeNode;
}

export function expandNodeById(treeState: ITreeState, ...ids: any[]): ITreeState {
  return {
    ...treeState,
    expandedNodeIds: {
      ...(treeState.expandedNodeIds || {}),
      ...fromPairs(ids.map((id) => [id, true])),
    },
  };
}

export function setActiveAndFocusedByNodeId(treeState: ITreeState, id: any): ITreeState {
  return {
    ...treeState,
    activeNodeIds: { [id]: true },
    focusedNodeId: id,
  };
}

export function goToNodeByPath(treeState: ITreeState, path: any[]): ITreeState {
  return setActiveAndFocusedByNodeId(expandNodeById(treeState, ...initial(path)), last(path));
}

export interface TreeHelper {
  createPath(...nodes: any[]): any[];

  findNextNode(roots: any[], removedPath: any[], selectedPath: any[]): any[];

  reduceNodes<U>(roots: any[],
                 reducer: (acc: U, node: any, index: number, siblings: any[], path: any[]) => U,
                 init: U): U;

  merge(thisRoots: any[], anotherRoots: any[], merger: (thisNode: any, anotherNode: any, level: number) => void): void;
}

export function createTreeHelper(structure: string[]): TreeHelper {
  return {
    createPath: (...nodes) => compact(nodes),
    findNextNode,
    reduceNodes,
    merge,
  };

  function merge(thisRoots: any[],
                 anotherRoots: any[],
                 merger: (thisNode: any, anotherNode: any, level: number) => void): void {
    let level = 0;
    let nextLevel: [any, any][] = [];
    let thisLevel: [any, any][] = [];

    fillNextLevel(thisRoots, anotherRoots);

    while (nextLevel.length) {
      thisLevel = nextLevel;
      nextLevel = [];

      while (thisLevel.length) {
        const [thisNode, anotherNode] = thisLevel.pop()!;

        merger(thisNode, anotherNode, level + 1);

        fillNextLevel(thisNode[structure[level]], anotherNode[structure[level]]);
      }

      level++;
    }

    function fillNextLevel(thisNodes: any[], anotherNodes: any[]): void {
      const minLength = Math.min(size(thisNodes), size(anotherNodes));
      for (let i = 0; i < minLength; i++) {
        nextLevel.push([thisNodes[i], anotherNodes[i]]);
      }
    }
  }

  function reduceNodes(roots: any[],
                       reducer: (acc: any, node: any, index: number, siblings: any[], path: any[]) => any,
                       init: any): any {
    type NodeDef = {
      node: any;
      index: number;
      siblings: any;
      path: any[];
    };

    let acc = init;
    const nodeDefs: NodeDef[] = [];

    forEachRight(roots, (root, i, siblings) => {
      nodeDefs.push({ node: root, index: i, siblings, path: [root] });
    });

    while (nodeDefs.length) {
      const { node, index, siblings, path } = nodeDefs.pop()!;

      acc = reducer(acc, node, index, siblings, path);

      if (path.length <= structure.length) {
        forEachRight(arrayify(node[structure[path.length - 1]]), (child, i, childSiblings) => {
          nodeDefs.push({ node: child, index: i, siblings: childSiblings, path: path.concat([child]) });
        });
      }
    }

    return acc;
  }

  function findNextNode(roots: any[], removedPath: any[], selectedPath: any): any[] {
    const differentIndex = findFirstDifferentIndex(removedPath, selectedPath);

    // When we did not find any different index it means that first part of both types is the same.
    // In this case we have to find next selected node iff removedPath is a subset of selectedPath
    if (differentIndex < 0 && removedPath.length <= selectedPath.length) {
      return findNextNodeWithout(roots, removedPath);
    } else {
      return selectedPath;
    }
  }

  function findNextNodeWithout(roots: any[], removedPath: any[]): any[] {
    if (!isNodeInTree(roots, removedPath)) {
      return [];
    }

    // try to find next sibling or use parent node
    return findNextSiblingNode(roots, removedPath) || initial(removedPath);
  }

  function findNextSiblingNode(roots: any[], removedPath: any[]): Maybe<any[]> {
    const siblings = getSiblings(roots, removedPath);
    const removed = last(removedPath);
    // Try to find index of the next element in its parent
    const nextIndex = clamp(siblings.indexOf(removed), -1, siblings.length - 2);
    if (nextIndex >= 0) {
      const nextNode = without(siblings, removed)[nextIndex];
      return initial(removedPath).concat([nextNode]);
    }
  }

  function isNodeInTree(roots: any[], removedPath: any[]): boolean {
    const withoutLast = initial(removedPath);
    const onlyLast = last(removedPath);
    const lastLevel = withoutLast.reduce(
      (nodes, pathFragment, i) => nodes.includes(pathFragment) ? pathFragment[structure[i]] : [],
      roots);
    return lastLevel.includes(onlyLast);
  }

  function getSiblings(roots: any[], removedPath: any[]): any[] {
    if (removedPath.length === 1) {
      return roots;
    } else {
      const parentIndex = removedPath.length - 2;
      return removedPath[parentIndex][structure[parentIndex]];
    }
  }
}

function findFirstDifferentIndex(removedPath: any[], selectedPath: any[]): number {
  const safeLength = Math.min(removedPath.length, selectedPath.length);
  for (let i = 0; i < safeLength; i++) {
    if (removedPath[i] !== selectedPath[i]) {
      return i;
    }
  }
  return -1;
}
